import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { extractErrorMessage } from '../utils/errorUtils';
import CastcoLogo from './CastcoLogo';

const Login: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 當錯誤狀態改變時，顯示/隱藏錯誤消息
  useEffect(() => {
    if (error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [error]);

  const handleUserIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserId(e.target.value);
    // 當用戶開始輸入時，隱藏錯誤消息
    if (error) {
      setError('');
      setShowError(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    // 當用戶開始輸入時，隱藏錯誤消息
    if (error) {
      setError('');
      setShowError(false);
    }
  };

  const handleCloseError = () => {
    setError('');
    setShowError(false);
  };

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement> | React.MouseEvent | React.KeyboardEvent) => {
    // 阻止所有可能的默認行為（防止頁面刷新）
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // 如果已經在加載中，不重複提交
    if (isLoading) {
      return false;
    }

    // 基本驗證
    if (!userId.trim() || !password.trim()) {
      setError('用戶名和密碼不能為空');
      setShowError(true);
      return false;
    }

    setError('');
    setShowError(false);
    setIsLoading(true);

    try {
      await login({ userId, password });
      // 登錄成功，清除任何錯誤狀態
      setError('');
      setShowError(false);

      // 使用 setTimeout 確保狀態更新完成後再導航
      setTimeout(() => {
        // 使用 replace 避免在歷史記錄中留下登錄頁面，並防止刷新
        navigate('/', { replace: true });
      }, 0);

      return true;
    } catch (err: any) {
      // 提取錯誤消息
      const errorMessage = extractErrorMessage(err, t('auth.loginFailed'));
      setError(errorMessage);
      setShowError(true);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit();
  };


  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <CastcoLogo size="large" variant="login"/>
        <Typography variant="h4" align="center" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
          {t('pages.login.title')}
        </Typography>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('auth.login')}
          </Typography>

          <Collapse in={showError && !!error}>
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleCloseError}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Collapse>

          <Box
            component="div"
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="userId"
              label={t('auth.username')}
              name="userId"
              autoComplete="username"
              autoFocus
              value={userId}
              onChange={handleUserIdChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!isLoading && userId.trim() && password.trim()) {
                    handleSubmit();
                  }
                }
              }}
              disabled={isLoading}
              error={!!error && showError}
              helperText={error && showError && error.includes('用戶名') ? error : ''}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!isLoading && userId.trim() && password.trim()) {
                    handleSubmit();
                  }
                }
              }}
              disabled={isLoading}
              error={!!error && showError}
              helperText={error && showError && error.includes('密碼') ? error : ''}
            />
            <Button
              type="button"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              onClick={handleButtonClick}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('auth.login')
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;